import { useMemo, useState } from 'react';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { Baseline, Copy as CopyIcon, Eraser, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const FAQS = [
    { q: 'What’s the difference between Title Case and Sentence case?', a: 'Title Case capitalises every major word (“The Old Man And The Sea”). Sentence case capitalises only the first letter of each sentence and any proper nouns (“The old man and the sea”).' },
    { q: 'What does camelCase produce?', a: 'It joins your words with no spaces and capitalises every word except the first — like a JavaScript variable name (“convertCaseNow”).' },
    { q: 'Does this send my text anywhere?', a: 'No. Case Converter runs entirely in your browser. Your text is never uploaded, logged, or stored.' },
    { q: 'What about accented characters?', a: 'They’re preserved. Uppercase “é” becomes “É”, lowercase stays lowercase.' },
    { q: 'Can I paste from Word or Google Docs?', a: 'Yes. Rich formatting is stripped — only the text is converted.' },
];

const HOW = [
    'Paste or type your text into the editor. Every conversion runs the moment you press a button, in your browser — no upload, no waiting, no server round-trip. You can switch between conversions freely; each one operates on the current text and updates it in place.',
    'The converter handles Unicode text correctly, so accented characters, non-Latin scripts, and emoji all survive the conversion unchanged where possible. For programmer-style transforms like camelCase, snake_case, and kebab-case, we split your input on any run of non-alphanumeric characters and re-join with the format you asked for.',
    'A copy-to-clipboard button lets you paste the converted result straight into whatever you’re writing. A single Reset undoes everything and restores your original text.',
    'Because it’s fully client-side, Case Converter works on any device with a browser — even offline once the page has loaded. Bookmark it on your phone for quick fixes on the go.',
];
const BENEFITS = [
    'The daily use case: fixing a headline someone typed in all caps, converting a URL slug back to a readable title, or making a screamed email polite before you paste it into a message. What used to take an awkward manual retype now takes one click.',
    'For developers, Case Converter is a fast way to move an identifier between conventions. A form field called “User Full Name” becomes “userFullName” for JavaScript, “user_full_name” for Python, or “user-full-name” for a CSS class. Consistent casing is one of those tiny quality signals that professional codebases get right, and this tool makes it effortless.',
    'For writers and editors, being able to instantly re-cast a paragraph as Title Case or Sentence case is a quiet productivity boost. Design mockups, article titles, deck slides, and social copy all benefit from the same rules applied consistently — and having a one-click converter beats retyping.',
];
const TIPS = [
    'Use Sentence case for most body copy — it’s the easiest to read.',
    'Use Title Case for headlines, product names, and buttons.',
    'Use kebab-case for URL slugs and CSS class names.',
    'Use snake_case for Python and Ruby identifiers.',
];

function titleCase(s) {
    return s.toLowerCase().replace(/(^|\s|[-_/])(\w)/g, (_, sep, ch) => sep + ch.toUpperCase());
}
function sentenceCase(s) {
    const lower = s.toLowerCase();
    return lower.replace(/(^|[.!?…]\s+)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());
}
function alternating(s) {
    let up = false;
    return Array.from(s).map((c) => {
        if (/[a-zà-ÿ]/i.test(c)) {
            up = !up;
            return up ? c.toUpperCase() : c.toLowerCase();
        }
        return c;
    }).join('');
}
function inverseCase(s) {
    return Array.from(s).map((c) => (c === c.toLowerCase() ? c.toUpperCase() : c.toLowerCase())).join('');
}
function tokens(s) {
    return s
        .replace(/[_\-\/]+/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .split(/\s+/)
        .filter(Boolean);
}
function camelCase(s) {
    const t = tokens(s).map((w) => w.toLowerCase());
    return t.map((w, i) => (i === 0 ? w : w[0].toUpperCase() + w.slice(1))).join('');
}
function pascalCase(s) {
    return tokens(s).map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase()).join('');
}
function snakeCase(s) {
    return tokens(s).map((w) => w.toLowerCase()).join('_');
}
function kebabCase(s) {
    return tokens(s).map((w) => w.toLowerCase()).join('-');
}

const OPS = [
    { key: 'upper', label: 'UPPERCASE', fn: (s) => s.toUpperCase() },
    { key: 'lower', label: 'lowercase', fn: (s) => s.toLowerCase() },
    { key: 'title', label: 'Title Case', fn: titleCase },
    { key: 'sentence', label: 'Sentence case', fn: sentenceCase },
    { key: 'camel', label: 'camelCase', fn: camelCase },
    { key: 'pascal', label: 'PascalCase', fn: pascalCase },
    { key: 'snake', label: 'snake_case', fn: snakeCase },
    { key: 'kebab', label: 'kebab-case', fn: kebabCase },
    { key: 'alt', label: 'aLtErNaTiNg', fn: alternating },
    { key: 'inverse', label: 'iNVERSE cASE', fn: inverseCase },
];

export default function CaseConverterPage() {
    const [text, setText] = useState('');
    const stats = useMemo(() => {
        const trimmed = text.trim();
        return {
            chars: text.length,
            words: trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0,
        };
    }, [text]);

    const apply = (fn) => setText((cur) => fn(cur));
    const clear = () => setText('');
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied to clipboard.');
        } catch {
            toast.error('Could not access clipboard.');
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Case Converter',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Case Converter"
                description="Convert text between UPPERCASE, lowercase, Title Case, Sentence case, camelCase, snake_case, kebab-case and more. 100% client-side."
                canonical="/tools/case-converter"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Case Converter' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <Baseline className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Case Converter</h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Instantly convert text between common cases — for prose, headlines,
                            slugs, and code.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">In-browser</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Unicode-safe</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.35fr_0.65fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <label htmlFor="cc-input" className="text-sm font-medium text-slate-800">Your text</label>
                        <Textarea
                            id="cc-input"
                            data-testid="tool-case-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={12}
                            placeholder="Paste or type your text here."
                            className="mt-2 min-h-[240px] leading-relaxed"
                        />
                        <div className="mt-3 flex items-center justify-between text-xs text-slate-500">
                            <span>{stats.words} words · {stats.chars} characters</span>
                            <div className="flex gap-2">
                                <Button size="sm" variant="secondary" onClick={copy} disabled={text.length === 0} data-testid="tool-case-copy">
                                    <CopyIcon className="w-3.5 h-3.5 mr-1.5" /> Copy
                                </Button>
                                <Button size="sm" variant="ghost" onClick={clear} disabled={text.length === 0} className="text-slate-600" data-testid="tool-case-clear">
                                    <Eraser className="w-3.5 h-3.5 mr-1.5" /> Clear
                                </Button>
                            </div>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Choose a case</p>
                            <div className="mt-4 grid grid-cols-2 gap-2">
                                {OPS.map((op) => (
                                    <Button
                                        key={op.key}
                                        variant="outline"
                                        onClick={() => apply(op.fn)}
                                        disabled={text.length === 0}
                                        data-testid={`tool-case-${op.key}`}
                                        className="justify-start font-normal text-slate-800"
                                    >
                                        {op.label}
                                    </Button>
                                ))}
                            </div>
                        </Card>
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Nothing leaves your browser</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                Case Converter runs entirely on your device. Nothing is
                                uploaded, logged, or stored.
                            </p>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
